import React from "react";
import { CatalogLoadState } from "@/components/common/CatalogLoadState";
import { usePublicData } from "@/lib/usePublicData";
import PageRenderer from "@/components/PageRenderer";
import Seo from "@/components/common/Seo";
import { useCatalog } from "@/lib/catalog";
import { ALT } from "@/locale/alternates";
import academyMedia from "@/lib/academy-media.json";
import { BestpreisHero, BESTPREIS_TRUST } from "@/components/BestpreisWidgets";
import { m as motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Crown, PlayCircle, Layers, FileText, Calculator, ArrowRight } from "lucide-react";
import { PromoHeroBanner } from "@/components/common/PromoKit";
import OrderTrackingPromo from "@/components/common/OrderTrackingPromo";
import { imgSet } from "@/lib/img";

/**
 * Home Page: komplett aus dem Page-Builder (Layout Oktober 2026: Aktion, Academy, Bestpreis usw. sind
 * eigene Sektionstypen). Wird im Admin eine ältere Version ohne diese Typen wiederhergestellt, werden
 * die früher fest eingebauten Blöcke (LegacyHomeExtras) wie gewohnt darunter angezeigt.
 */
export default function Home() {
  const { data: page, loading, error, retry } = usePublicData("/public/pages/home");
  const sections = page?.sections || [];
  const { products: catalog } = useCatalog();
  const homeMin = Math.min(...(catalog || []).flatMap((p) => (p.packages || []).map((k) => Number(k.price_gross || 0))).filter((x) => x > 0)) || 0;
  if (loading || error) return (
    <div className="min-h-[40vh] max-w-[1400px] mx-auto px-6 md:px-10 py-16">
      <CatalogLoadState loading={loading} error={error} onRetry={retry} id="home-content" />
    </div>
  );
  const newLayout = sections.some((s) => s.type === "academy");

  return (
    <div data-testid="home-page">
      <Seo
        title={`Fotobox kaufen${Number.isFinite(homeMin) && homeMin ? ` ab ${homeMin.toLocaleString("de-DE")} €` : ""}: Profi-Fotoboxen mit Sofortdruck`}
        description={`Fotobox kaufen für Vermietung und Business: Fotobox Premium, Magic Mirror und 360° Videobooth${Number.isFinite(homeMin) && homeMin ? ` ab ${homeMin.toLocaleString("de-DE")} €` : ""}. Versand in Deutschland inklusive, Ratenkauf, Showroom Bottrop.`}
        path="/"
        alternates={ALT.home()}
      />
      <PageRenderer sections={sections} />
      {!newLayout && <LegacyHomeExtras />}
    </div>
  );
}

/** Früher fest eingebaute Startseiten-Blöcke, nur für ältere Layout-Versionen. */
function LegacyHomeExtras() {
  return (
    <>
      {/* --- Herbst-Aktion Hero-Banner (nur aktiv während Promo) --- */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10" data-testid="home-promo">
        <PromoHeroBanner />
      </section>

      {/* --- Fotobox Academy Pro · exklusiv im Komplettpaket --- */}
      <section className="relative overflow-hidden bg-ink text-white" data-testid="home-academy">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(212,175,55,0.4), transparent 40%), radial-gradient(circle at 80% 70%, rgba(212,175,55,0.2), transparent 55%)" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-yellow/50 bg-brand-yellow/10 text-brand-yellow text-[11px] uppercase tracking-widest">
              <Crown className="w-3.5 h-3.5" /> Exklusiv im Komplettpaket
            </div>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05]" data-testid="home-academy-title">Ihre Fotobox kann mehr. <span className="text-brand-yellow">Wir zeigen Ihnen, wie.</span></h2>
            <p className="mt-4 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="home-academy-description">
              Nicht nur eine Fotobox kaufen, auch an die Fragen danach denken. Mit der <b>Fotobox Academy Pro</b> entsteht der Schulungs- und Werkzeugbereich für unsere Käufer: Technik verstehen, Vermietungen vorbereiten und die eigenen Zahlen im Blick behalten. Die Zugangsberechtigung gehört zu Ihrem Komplettpaket.
            </p>
            <p className="mt-4 text-white leading-relaxed" data-testid="home-academy-status"><strong>Derzeit im Aufbau:</strong> Die Online-Freischaltung wird vorbereitet. Videos und ergänzende Anleitungen werden anschließend nach und nach veröffentlicht; die 199 Videolektionen sind noch nicht vollständig verfügbar.</p>
            <p className="mt-4 text-sm text-white/80" data-testid="home-academy-planned-scope">Geplanter Umfang: 199 Videolektionen · 21 Themenbereiche · 8 Hauptkategorien</p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: PlayCircle, label: "Videolektionen geplant" },
                { icon: Layers, label: "Schrittweiser Aufbau" },
                { icon: FileText, label: "Vertragswerkzeug" },
                { icon: Calculator, label: "Gewinnkalkulator" },
              ].map((b, i) => (
                <div key={b.label} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 flex items-center gap-2 text-sm" data-testid={`home-academy-feature-${i}`}>
                  <b.icon className="w-4 h-4 shrink-0 text-brand-yellow" /> <span className="min-w-0 break-words">{b.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/fotobox-pro-academy" className="btn-accent" data-testid="home-academy-cta">Mehr zur Academy erfahren <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/produkte" className="text-white/70 hover:text-white underline underline-offset-4 self-center text-sm" data-testid="home-academy-products">Komplettsysteme ansehen</Link>
            </div>
          </div>
          <div className="rounded-3xl bg-white/5 border border-white/10 p-6 md:p-8 backdrop-blur-sm" data-testid="home-academy-benefits">
            <Link to="/fotobox-pro-academy" className="block rounded-xl overflow-hidden focus-visible:ring-2 focus-visible:ring-brand-yellow" data-testid="home-academy-preview-link" aria-label="Designvorschau ansehen und mehr zur Fotobox Academy Pro erfahren">
              <img {...imgSet(`${process.env.REACT_APP_BACKEND_URL}${academyMedia.overview.src}`, "(max-width: 768px) 100vw, 50vw")} width={academyMedia.overview.width} height={academyMedia.overview.height} alt={academyMedia.overview.alt} loading="lazy" decoding="async" className="block w-full h-auto" data-testid="home-academy-preview-image" />
            </Link>
            <p className="mt-3 mb-6 text-xs leading-relaxed text-white/75" data-testid="home-academy-preview-caption">Designvorschau · Fotobox Academy Pro im Aufbau. Abgebildete Rechenwerte sind Beispiele, keine Ertragszusage.</p>
            <div className="text-xs uppercase tracking-widest text-brand-yellow font-semibold">Werkzeuge für Ihren Vermietungsalltag</div>
            <p className="mt-3 text-sm text-white/85 leading-relaxed">Technisch funktionsfähig. Online-Freischaltung in Vorbereitung.</p>
            <ul className="mt-4 space-y-3 text-sm">
              {["Mietvertrags-Generator für eine vollständige Vertrags-PDF", "Gewinnkalkulator auf Grundlage Ihrer Angaben", "Persönlicher Kundenbereich für Ausstattung und Lizenzlaufzeiten"].map((text, i) => (
                <li key={text} className="flex items-start gap-2" data-testid={`home-academy-tool-${i}`}><span className="text-brand-yellow" aria-hidden="true">✓</span> <span className="text-white/85">{text}</span></li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-white/85 leading-relaxed" data-testid="home-academy-tools-status">Technisch funktionsfähig bedeutet nicht bereits online zugänglich. Die Online-Freischaltung der Fotobox Academy Pro wird vorbereitet.</p>
          </div>
        </div>
      </section>

      {/* --- Trust-Icons (zwischen „Bereit für die eigene Fotobox?" und Bestpreis) --- */}
      <section className="py-14 md:py-20 bg-white" data-testid="home-trust">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {BESTPREIS_TRUST.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.04, duration: 0.4 }}
                className="rounded-2xl bg-white border border-hair p-4 md:p-5 flex flex-col items-start hover:border-brand-yellow/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-brand-yellow/15 flex items-center justify-center mb-2">
                  <it.icon className="w-5 h-5 text-brand-yellow" strokeWidth={2.2} />
                </div>
                <div className="font-heading text-base text-ink leading-tight">{it.title}</div>
                <div className="mt-1 text-xs text-ink-soft leading-snug">{it.text}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Nach der Bestellung nicht allein: Kundenkonto mit Produktionsstatus (27.10.2026) --- */}
      <OrderTrackingPromo testid="home-order-tracking" />

      {/* --- Bestpreisgarantie-Section --- */}
      <section className="py-16 md:py-24 bg-cream border-b border-hair" data-testid="home-bestpreis">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <BestpreisHero />
        </div>
      </section>
    </>
  );
}
